// JAA-388: browsers can deny localStorage / sessionStorage / document.cookie
// (blocked site data, locked-down or misconfigured WebViews, sandboxed frames).
// In Chromium the denial is thrown by reading the `window.localStorage`
// property itself, so a try/catch around getItem alone is not enough. This
// module is the only place allowed to touch these APIs (enforced by an ESLint
// rule): every call resolves the real storage inside its own try/catch and
// falls back to an in-memory copy, so a blocked storage behaves like "nothing
// was saved" instead of crashing the app.

type StorageKind = 'localStorage' | 'sessionStorage';

export interface SafeStorage {
  getItem(key: string): string | null;
  /**
   * Like getItem, but throws when the browser storage cannot be read, so a
   * caller can tell "never saved" from "unreadable". Skips the in-memory copy:
   * use it with setItemOrThrow where only a durable value counts.
   */
  getItemOrThrow(key: string): string | null;
  /** Parsed JSON value, or null when missing, unparseable (corrupt) or unreadable. */
  getJSON<T>(key: string): T | null;
  setItem(key: string, value: string): void;
  /**
   * Like setItem, but throws when the value could not be saved durably. Use it
   * where the value must outlive this page (it is read after a redirect or by
   * another tab): the in-memory fallback would silently lose it.
   */
  setItemOrThrow(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  keys(): string[];
}

/**
 * Builds a storage facade over `window[kind]` with an in-memory fallback.
 * Writes go to the real storage and land in memory only when that write fails;
 * reads return that memory copy first (it is newer), then the real storage. A
 * healthy browser never uses the memory copy. The *OrThrow pair skips memory
 * and throws instead, for values that must survive a reload.
 */
function createSafeStorage(kind: StorageKind): SafeStorage {
  const memory = new Map<string, string>();

  // Resolving the property can itself throw, so it lives inside the guard.
  const real = (): Storage | null => {
    try {
      return window[kind] ?? null;
    } catch {
      return null;
    }
  };

  const storage: SafeStorage = {
    getItem(key) {
      // Memory holds only values whose durable write failed, so it is newer than storage.
      const fallback = memory.get(key);
      if (fallback !== undefined) return fallback;
      try {
        return real()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    getItemOrThrow(key) {
      const area = real();
      if (!area) throw new Error('Browser storage is unavailable');
      return area.getItem(key);
    },
    getJSON<T>(key: string): T | null {
      try {
        const raw = storage.getItem(key);
        return raw == null ? null : (JSON.parse(raw) as T);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        const area = real();
        if (area) {
          area.setItem(key, value);
          memory.delete(key);
          return;
        }
      } catch {
        // fall through to memory
      }
      memory.set(key, value);
    },
    setItemOrThrow(key, value) {
      const area = real();
      if (!area) throw new Error('Browser storage is unavailable');
      area.setItem(key, value);
      memory.delete(key);
    },
    removeItem(key) {
      memory.delete(key);
      try {
        real()?.removeItem(key);
      } catch {
        // nothing stored in a blocked storage
      }
    },
    clear() {
      memory.clear();
      try {
        real()?.clear();
      } catch {
        // nothing stored in a blocked storage
      }
    },
    keys() {
      const found = new Set(memory.keys());
      try {
        const area = real();
        for (let i = 0; area && i < area.length; i++) {
          const key = area.key(i);
          if (key != null) found.add(key);
        }
      } catch {
        // blocked storage has no keys beyond memory
      }
      return Array.from(found);
    },
  };
  return storage;
}

export const localStore = createSafeStorage('localStorage');
export const sessionStore = createSafeStorage('sessionStorage');

/**
 * Returns the raw `document.cookie` string, or '' when the browser denies
 * cookie access (sandboxed frames throw a SecurityError on read).
 */
export function readCookies(): string {
  try {
    return document.cookie;
  } catch {
    return '';
  }
}

/**
 * Sets one cookie from a full `name=value; attributes` string. A denied write
 * is ignored: callers only use this for best-effort clean-up.
 */
export function writeCookie(cookie: string): void {
  try {
    document.cookie = cookie;
  } catch {
    // cookies unavailable: nothing to write
  }
}
